import {
    Children,
    cloneElement,
    isValidElement,
    type HTMLAttributes,
    type ReactElement,
    type ReactNode,
} from 'react';
import { cn } from '../ui/utils';

type FieldProps = HTMLAttributes<HTMLDivElement> & {
    label: string;
    htmlFor: string;
    error?: string;
    hint?: string;
    children: ReactNode;
    /** Tooltip do label (ex.: rótulo curto com título completo). */
    labelTitle?: string;
};

/**
 * Wrapper de label + controle + erro + hint.
 * Propaga `aria-describedby` e `aria-invalid` ao filho de formulário.
 */
function Field({ label, htmlFor, error, hint, children, className, labelTitle, ...props }: FieldProps) {
    const errorId = `${htmlFor}-error`;
    const hintId = `${htmlFor}-hint`;

    const childrenArray = Children.toArray(children);
    const firstChild = childrenArray[0];

    const control =
        childrenArray.length === 1 && isValidElement(firstChild)
            ? cloneElement(firstChild as ReactElement<Record<string, unknown>>, {
                  id: (firstChild.props as { id?: string }).id ?? htmlFor,
                  'aria-describedby': error
                      ? errorId
                      : hint
                        ? hintId
                        : (firstChild.props as { 'aria-describedby'?: string })['aria-describedby'],
                  'aria-invalid': error
                      ? true
                      : (firstChild.props as { 'aria-invalid'?: boolean | 'true' | 'false' })[
                            'aria-invalid'
                        ],
                  error:
                      error != null
                          ? true
                          : (firstChild.props as { error?: boolean }).error,
              })
            : children;

    return (
        <div className={cn('nx-field', className)} {...props}>
            <label htmlFor={htmlFor} className="nx-field-label" title={labelTitle}>
                {label}
            </label>
            {control}
            {hint ? (
                <span id={hintId} className="nx-form-hint">
                    {hint}
                </span>
            ) : null}
            {error ? (
                <span id={errorId} className="nx-field-error" role="alert">
                    {error}
                </span>
            ) : null}
        </div>
    );
}

export { Field };
export type { FieldProps };

